<script setup>
import { cookieRef } from '@/@layouts/stores/config'
import { useFeedbackEntry } from '@core/stores/feedback-entry'
import AssortedFeedbackQuestion from '@/views/feedback/feedback-entry/tabs/AssortedFeedbackQuestion.vue'
import { avatarText, formatDate } from '@/@core/utils/formatters'
import { refDebounced } from '@vueuse/core'
import { VSkeletonLoader } from 'vuetify/lib/labs/components.mjs'
import moment from 'moment'

definePage({
  meta: {
    name: "Ward Round V2",
    layout: 'blank',
    subject: 'feedback.menu',
    action: 'feedback_entry_ward_round',
  },
})

const snackbar = useToast()

const unitId = cookieRef('selectedUnit')

// State
const currentStep = ref(1) // 1 = patient selection, 2 = feedback
const selectedCustomer = ref(null)
const selectedEpisode = ref(null)
const searchQuery = ref('')
const debouncedSearch = refDebounced(searchQuery, 1000)
const formType = ref(null)
const selectedEpisodeId = ref(null)

// Pagination State
const currentPage = ref(1)
const itemsPerPage = 10
const hasMore = ref(true)
const isLoadingMore = ref(false)
const customersList = ref([])

// Advanced Search State
const showAdvancedSearch = ref(false)
const selectedUnit = ref(unitId.value)
const selectedUnitArray = computed(() => selectedUnit.value ? [selectedUnit.value] : [])
const selectedLocation = ref(null)
const today = moment().format('YYYY-MM-DD')

// Date range - separate refs for display and actual values
const defaultDateRange = `${today} to ${today}`
const dateRange = ref(defaultDateRange)
const fromDate = ref(today)
const toDate = ref(today)

// Date Range Picker Config with onChange handler
const dateRangeConfig = {
  mode: 'range',
  dateFormat: 'Y-m-d',
  maxDate: new Date(),
  defaultDate: [today, today],
  onChange: (selectedDates, dateStr) => {
    if (selectedDates.length > 0) {
      fromDate.value = moment(selectedDates[0]).format('YYYY-MM-DD')
      toDate.value = selectedDates[1]
        ? moment(selectedDates[1]).format('YYYY-MM-DD')
        : fromDate.value
    }
  },
}

// API: Get Ward Round Source
const sourceType = ref(null)

await useApi(createUrl('masters/feedback-sources', {
  query: {
    search: {
      code: 'WARD-ROUND',
    },
  },
}), {
  afterFetch: ({ data }) => {
    sourceType.value = data[0]?.id ?? null
  },
})

// API: Form Types for filter
const { data: formTypes } = await useApi(createUrl('masters/form-types', {
  query: {
    unit_id: selectedUnit,
    select: ['id', 'name'],
  },
}), {
  afterFetch: () => {
    formType.value = null
  },
})

// API: Units for filter
const { data: units } = await useApi(createUrl('masters/units', {
  query: {
    select: ['id', 'name'],
  },
}))

// Location filter state
const searchLocation = ref('')
const debouncedSearchLocation = refDebounced(searchLocation, 500)

// API: Locations for filter (based on selected unit)
const { data: locations, execute: fetchLocations } = useApi(createUrl('masters/locations', {
  query: {
    select: ['id', 'name'],
    limit: 10,
    q: debouncedSearchLocation,
    search: {
      unit_id: selectedUnit,
    },
  },
}), { 
  beforeFetch: async ({ url, options, cancel }) => {
    if (!searchLocation.value) cancel()
  },
  immediate: false,
})

// Fetch locations when unit changes
watch(selectedUnit, () => {
  selectedLocation.value = null
  searchLocation.value = ''
  if (selectedUnit.value) {
    fetchLocations()
  }
}, { immediate: true })

// Doctor filter state
const searchDoctor = ref('')
const debouncedSearchDoctor = refDebounced(searchDoctor, 500)
const selectedDoctor = ref(null)

// Specialty filter state
const searchSpecialty = ref('')
const debouncedSearchSpecialty = refDebounced(searchSpecialty, 500)
const selectedSpecialty = ref(null)

// Feedback Status filter state (All, Collected, Not Collected)
const feedbackStatus = ref('all')

// API: Doctors for filter (based on unit and form type)
const { data: doctors, execute: fetchDoctors } = useApi(createUrl('masters/episode/doctors', {
  query: {
    limit: 10,
    q: debouncedSearchDoctor,
    search: {
      unit_id: selectedUnit,
      form_type_id: formType,
    },
  },
}), { immediate: false })

// Fetch doctors when unit or form type changes
watch([selectedUnit, formType], () => {
  selectedDoctor.value = null
  fetchDoctors()
}, { immediate: true })

// API: Specialties for filter
const { data: specialties, execute: fetchSpecialties } = useApi(createUrl('masters/episode/specialties', {
  query: {
    limit: 10,
    q: debouncedSearchSpecialty,
    search: {
      unit_id: selectedUnit,
      form_type_id: formType,
    },
  },
}), { immediate: false })

// Fetch specialties on search
watch(debouncedSearchSpecialty, () => {
  if (debouncedSearchSpecialty.value) {
    fetchSpecialties()
  }
}, { immediate: true })

// Computed episode search params for API
const episodeSearchParams = computed(() => {
  const params = {
    'episodes.unit_id': selectedUnit.value,
  }

  if (formType.value) {
    params['episodes.form_type_id'] = formType.value
  }

  if (selectedLocation.value) {
    params['episodes.location_id'] = selectedLocation.value
  }

  if (selectedDoctor.value) {
    params['episodes.doctor_name'] = selectedDoctor.value
  }

  if (selectedSpecialty.value) {
    params['episodes.specialty'] = selectedSpecialty.value
  }

  // Use fromDate and toDate directly as array
  if (fromDate.value && toDate.value) {
    params['episodes.visit_date_time'] = [fromDate.value, toDate.value]
  }

  return params
})

// Computed feedback status params
const episodeFeedbacksParam = computed(() => {
  // All & Collected = true, Not Collected = false
  return feedbackStatus.value !== 'not_collected'
})

const episodeHaveFeedbacksParam = computed(() => {
  // All = null (not sent), Collected = true, Not Collected = false
  if (feedbackStatus.value === 'all') return null
  if (feedbackStatus.value === 'collected') return true

  return false
})

const {
  isFetching,
  execute: executeCustomersFetch,
} = useApi(createUrl('/masters/customers', {
  query: {
    q: debouncedSearch,
    limit: itemsPerPage,
    page: currentPage,
    episode: true,
    episode_feedbacks: episodeFeedbacksParam,
    episode_have_feedbacks: episodeHaveFeedbacksParam,
    search: episodeSearchParams,
  },
}), {
  beforeFetch ({ url, options, cancel }) {
    if (!formType.value) {
      cancel()
    }
  },
  afterFetch: ({ data }) => {
    if (currentPage.value === 1) {
      customersList.value = data || []
    } else {
      customersList.value = [...customersList.value, ...(data || [])]
    }

    // Check if there are more records
    hasMore.value = data?.length === itemsPerPage
    isLoadingMore.value = false
  },
  onFetchError: () => {
    // isLoadingMore.value = false // NOT WORKING
  },
})

// Computed customers for template
const customers = computed(() => customersList.value)

// Fetch customers (reset pagination)
const fetchCustomers = () => {
  currentPage.value = 1
  hasMore.value = true
  customersList.value = []
  selectedEpisodeId.value = null
  executeCustomersFetch()
}

// Load more customers
const loadMore = () => {
  if (!hasMore.value || isFetching.value || isLoadingMore.value) return
  isLoadingMore.value = true
  currentPage.value += 1

  executeCustomersFetch()
}

// Reset advanced filters
const resetAdvancedFilters = () => {
  // formType.value = null

  selectedUnit.value = unitId.value
  selectedLocation.value = null
  searchLocation.value = ''
  selectedDoctor.value = null
  searchDoctor.value = ''
  selectedSpecialty.value = null
  searchSpecialty.value = ''
  feedbackStatus.value = 'all'

  // Reset date range - set both display string and actual values
  dateRange.value = defaultDateRange
  fromDate.value = today
  toDate.value = today

  // Auto refresh search after reset
  nextTick(() => {
    if (debouncedSearch.value && debouncedSearch.value.length >= 2) {
      fetchCustomers()
    }
  })
}

// Patient Selection Handler
const selectPatientEpisode = () => {
  if (!selectedEpisodeId.value) return null

  selectedCustomer.value = customers.value?.find(p => p.episodes?.some(ep => ep.id === selectedEpisodeId.value))
  selectedEpisode.value = selectedCustomer.value?.episodes?.find(ep => ep.id === selectedEpisodeId.value)

  // Get Customer Phone Number From Episode If Not Available
  // selectedCustomer.value.phone = selectedCustomer.value?.phone ?? selectedEpisode.value?.whatsapp_number

  // Add Country Code If Missing
  if (selectedCustomer.value?.phone?.length === 10) {
    selectedCustomer.value.phone = (selectedEpisode.value?.country_code ?? '+91') + selectedCustomer.value.phone
  }

  // Update store
  useFeedbackEntry().updateCurrentFeedback({
    feedback_type: 'assorted',
    customer_type: 'PATIENT',
    unit_id: unitId.value,
    source_type: sourceType.value,
    form_type: selectedEpisode.value?.form_type?.id,
    customer: {
      customer_id: selectedCustomer.value?.id,
      episode_id: selectedEpisode.value?.id,
      episode: selectedEpisode.value,
      episode_no: selectedEpisode.value?.code,
      unit_id: selectedEpisode.value?.unit_id,
      location: selectedEpisode.value?.location,
      name: selectedCustomer.value?.name,
      uhid: selectedCustomer.value?.uhid,
      age: selectedCustomer.value?.age,
      phone: selectedCustomer.value?.phone,
      email: selectedCustomer.value?.email,
      feedback_datetime: selectedEpisode.value?.visit_date_time,
    },
    feedback: [],
  })

  currentStep.value = 2
}

// Change Patient Handler
const changePatient = () => {
  selectedCustomer.value = null
  selectedEpisode.value = null
  selectedEpisodeId.value = null
  useFeedbackEntry().resetCurrentFeedback()
  currentStep.value = 1
}

// Feedback Form Data
const feedbackFormData = ref([])

watch(feedbackFormData, () => {
  if (Array.isArray(feedbackFormData.value) && feedbackFormData.value.length > 0) {
    useFeedbackEntry().updateCurrentFeedback({
      feedback: feedbackFormData.value.filter(item => {
        if (item.response !== null && item.response !== '' && item.response !== undefined) {
          return item
        }
      }),
    })
  }
}, { immediate: true, deep: true })

// Submit Handler
const loading = ref(false)

const customDateFormat = date => {
  const pad = n => n.toString().padStart(2, '0')

  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ` +
         `${pad(date.getHours())}:${pad(date.getMinutes())}`
}

const onSubmit = async () => {
  loading.value = true
  useFeedbackEntry().currentFeedback.customer.feedback_datetime = customDateFormat(new Date())

  await useFeedbackEntry().submitCurrentFeedback(
    false,
    () => {
      // Success callback - reset for next patient
      useFeedbackEntry().resetCurrentFeedback()
      selectedCustomer.value = null
      selectedEpisode.value = null
      selectedEpisodeId.value = null
      feedbackFormData.value = []
      searchQuery.value = ''
      currentStep.value = 1
      loading.value = false
    },
  )
  loading.value = false
}

// Lifecycle
onBeforeMount(() => {
  useFeedbackEntry().resetCurrentFeedback()
})

// Check if episode is already selected for a patient
const isEpisodeSelected = (patient, episode) => {
  return selectedEpisodeId.value === episode.id
}

// Handle episode selection within a card (toggle selection)
const handleEpisodeSelect = (patient, episode) => {
  if (selectedEpisodeId.value === episode.id) {
    selectedEpisodeId.value = null // Deselect if already selected
  } else {
    selectedEpisodeId.value = episode.id
  }
}
</script>

<template>
  <div class="ward-round-page">
    <!-- Error State: Ward Round Source not configured -->
    <div
      v-if="!sourceType"
      class="d-flex align-center justify-center"
      style="min-height: 100vh;"
    >
      <VAlert
        variant="tonal"
        color="error"
        icon="tabler-alert-triangle"
        max-width="500"
      >
        Ward Round (WARD-ROUND) Source is not configured. Please contact administrator.
      </VAlert>
    </div>

    <!-- Main Content -->
    <div
      v-else
      class="ward-round-container"
    >
      <!-- STEP 1: Patient Selection -->
      <div
        v-if="currentStep === 1"
        class="patient-selection-step"
      >
        <div class="patient-selection-content">
          <!-- Search Header (Sticky) -->
          <div class="search-header-sticky pa-4">
            <VCard
              variant="flat"
              class="border"
            >
              <VCardText>
                <VRow dense>
                  <VCol
                    cols="12"
                    md="6"
                    sm="6"
                  >
                    <AppTextField
                      v-model="searchQuery"
                      placeholder="Search by UHID or Mobile Number"
                      clearable
                      autofocus
                      class="flex-1-0"                                                                                     
                    >
                      <template #prepend-inner>
                        <VIcon
                          icon="tabler-search"
                          size="22"
                        />
                      </template>
                    </AppTextField>
                  </VCol>

                  <VCol
                    cols="12"
                    md="3"
                    sm="2"
                  >
                    <VSelect
                      v-model="formType"
                      :items="formTypes ?? []"
                      item-title="name"
                      item-value="id"
                      placeholder="Select Form Type"
                    />
                  </VCol>

                  <VCol
                    cols="12"
                    md="3"
                    sm="4"
                  >
                    <VBtn
                      variant="flat"
                      color="primary"
                      style="margin-block-start: 1px;"
                      block
                      :prepend-icon="showAdvancedSearch ? 'tabler-chevron-up' : 'tabler-filter'"
                      @click="showAdvancedSearch = !showAdvancedSearch"
                    >
                      {{ showAdvancedSearch ? 'Hide Search' : 'Advanced Search' }}
                    </VBtn>
                  </VCol>
                </VRow>

                <!-- Advanced Search Filters (Collapsible) -->
                <VExpandTransition>
                  <div v-show="showAdvancedSearch">
                    <VDivider class="mt-6 mb-4" />
                    <VRow dense>
                      <!-- Unit -->
                      <VCol
                        cols="12"
                        md="6"
                      >
                        <AppSelect
                          v-model="selectedUnit"
                          :items="units ?? []"
                          item-title="name"
                          item-value="id"
                          label="Unit"
                          placeholder="Select Unit"
                          :disabled="units.length === 1"
                        />
                      </VCol>

                      <!-- Date Range -->
                      <VCol
                        cols="12"
                        md="6"
                      >
                        <AppDateTimePicker
                          v-model="dateRange"
                          label="Visit Date Range"
                          placeholder="Select Date Range"
                          :config="dateRangeConfig"
                        />
                      </VCol>

                      <!-- Location -->
                      <VCol
                        cols="12"
                        md="6"
                      >
                        <AppAutocomplete
                          v-model="selectedLocation"
                          v-model:search="searchLocation"
                          :items="locations ?? []"
                          item-title="name"
                          item-value="id"
                          label="Location"
                          placeholder="Search Location"
                          clearable
                          :disabled="!selectedUnit"
                          :return-object="false"
                        />
                      </VCol>

                      <!-- Doctor -->
                      <VCol
                        cols="12"
                        md="6"
                      >
                        <AppAutocomplete
                          v-model="selectedDoctor"
                          v-model:search="searchDoctor"
                          :items="doctors ?? []"
                          item-title="doctor_name"
                          item-value="doctor_name"
                          label="Doctor"
                          placeholder="Search Doctor"
                          clearable
                          :return-object="false"
                        />
                      </VCol>

                      <!-- Specialty -->
                      <VCol
                        cols="12"
                        md="6"
                      >
                        <AppAutocomplete
                          v-model="selectedSpecialty"
                          v-model:search="searchSpecialty"
                          :items="specialties ?? []"
                          item-title="specialty"
                          item-value="specialty"
                          label="Specialty"
                          placeholder="Search Specialty"
                          clearable
                          :return-object="false"
                        />
                      </VCol>

                      <!-- Feedback Status -->
                      <!--
                        <VCol
                        cols="12"
                        md="6"
                        >
                        <label
                        class="text-body-2 text-high-emphasis d-block"
                        style="margin-block-end: 5px;"
                        >
                        Feedback Collection
                        </label>
                        <VBtnToggle
                        v-model="feedbackStatus"
                        mandatory
                        color="primary"
                        variant="outlined"
                        divided
                        class="w-100 feedback-status-toggle"
                        >
                        <VBtn
                        value="all"
                        class="flex-grow-1"
                        >
                        All
                        </VBtn>
                        <VBtn
                        value="collected"
                        class="flex-grow-1"
                        >
                        Collected
                        </VBtn>
                        <VBtn
                        value="not_collected"
                        class="flex-grow-1"
                        >
                        Not Collected
                        </VBtn>
                        </VBtnToggle>
                        </VCol> 
                      -->

                      <VCol
                        cols="12"
                        md="6"
                      >
                        <AppSelect
                          v-model="feedbackStatus"
                          :items="[
                            { value: 'all', title: 'All' },
                            { value: 'collected', title: 'Collected' },
                            { value: 'not_collected', title: 'Not Collected' },
                          ]"
                          label="Feedback Collection"
                          placeholder=""
                        />
                      </VCol>

                      <!-- Reset Button -->
                      <VCol
                        cols="12"
                        class="mt-2 d-flex justify-end"
                      >
                        <VBtn
                          variant="tonal"
                          color="secondary"
                          size="small"
                          @click="resetAdvancedFilters"
                        >
                          <VIcon
                            icon="tabler-refresh"
                            size="18"
                            class="me-1"
                          />
                          Reset Filters
                        </VBtn>
                      </VCol>
                    </VRow>
                  </div>
                </VExpandTransition>

                <!-- Active Filter Chips (when advanced search is collapsed) -->
                <div
                  v-if="!showAdvancedSearch"
                  class="d-flex flex-wrap gap-2 mt-4"
                >
                  <!-- Unit Chip -->
                  <VChip
                    v-if="selectedUnit"
                    size="small"
                    color="primary"
                    variant="tonal"
                  >
                    <VIcon
                      icon="tabler-building-hospital"
                      size="14"
                      class="me-1"
                    />
                    {{ units?.find(x => x.id === selectedUnit)?.name || 'Unit' }}
                  </VChip>

                  <!-- Date Range Chip -->
                  <VChip
                    size="small"
                    color="primary"
                    variant="tonal"
                  >
                    <VIcon
                      icon="tabler-calendar"
                      size="14"
                      class="me-1"
                    />
                    {{ fromDate === toDate ? fromDate : dateRange }}
                  </VChip>

                  <!-- Location Chip -->
                  <VChip
                    v-if="selectedLocation"
                    size="small"
                    color="primary"
                    variant="tonal"
                  >
                    <VIcon
                      icon="tabler-map-pin"
                      size="14"
                      class="me-1"
                    />
                    {{ locations?.find(x => x.id === selectedLocation)?.name || 'Location' }}
                  </VChip>

                  <!-- Doctor Chip -->
                  <VChip
                    v-if="selectedDoctor"
                    size="small"
                    color="primary"
                    variant="tonal"
                  >
                    <VIcon
                      icon="tabler-stethoscope"
                      size="14"
                      class="me-1"
                    />
                    {{ selectedDoctor }}
                  </VChip>

                  <!-- Specialty Chip -->
                  <VChip
                    v-if="selectedSpecialty"
                    size="small"
                    color="primary"
                    variant="tonal"
                  >
                    <VIcon
                      icon="tabler-medical-cross"
                      size="14"
                      class="me-1"
                    />
                    {{ specialties?.find(x => x.id === selectedSpecialty)?.name || 'Specialty' }}
                  </VChip>

                  <!-- Feedback Status Chip -->
                  <VChip
                    v-if="feedbackStatus !== 'all'"
                    size="small"
                    color="primary"
                    variant="tonal"
                  >
                    <VIcon
                      icon="tabler-message-check"
                      size="14"
                      class="me-1"
                    />
                    {{ feedbackStatus === 'collected' ? 'Collected' : 'Not Collected' }}
                  </VChip>
                </div>
              </VCardText>
            </VCard>
          </div>

          <!-- Patient Results -->
          <div class="patient-results px-4 pb-16">
            <div v-if="!formType">
              <!-- Warning: Form Type not selected -->
              <VAlert
                v-if="!formType"
                variant="tonal"
                color="warning"
                icon="tabler-alert-triangle"
                class="mb-4"
              >
                Please select a Form Type to search for patients.
              </VAlert>
            </div>

            <div v-else>
              <!-- Loading State -->
              <div
                v-if="isFetching"
                class="d-flex flex-column gap-4"
              >
                <VSkeletonLoader
                  v-for="i in 3"
                  :key="i"
                  type="card"
                  height="200"
                />
              </div>

              <!-- Results List (One card per row) -->
              <div
                v-else-if="customers?.length"
                class="d-flex flex-column gap-4"
              >
                <VCard
                  v-for="patient in customers"
                  :key="patient.id"
                  variant="flat"
                  class="patient-card border"
                >
                  <!-- Patient Header -->
                  <VCardText>
                    <div class="d-flex align-center gap-3">
                      <VAvatar
                        color="primary"
                        variant="tonal"
                        size="48"
                      >
                        <VImg
                          v-if="patient?.avatar"
                          :src="patient?.avatar"
                        />
                        <span v-else>{{ avatarText(patient?.name) }}</span>
                      </VAvatar>
                      <div class="flex-grow-1">
                        <h6 class="text-h6 mb-0">
                          {{ patient.name }}
                        </h6>
                        <span class="text-body-2 text-disabled">UHID: {{ patient.uhid }}</span>
                      </div>
                    </div>

                    <!-- Patient Details (Compact) -->
                    <div class="d-flex flex-wrap align-center gap-x-4 gap-y-1 mt-3 text-body-2">
                      <span class="d-flex align-center gap-1">
                        <VIcon
                          icon="tabler-calendar"
                          size="16"
                          color="disabled"
                        />
                        Age: {{ patient.age || 'N/A' }}
                      </span>
                      <span class="d-flex align-center gap-1">
                        <VIcon
                          icon="tabler-phone"
                          size="16"
                          color="disabled"
                        />
                        {{ patient.phone || 'N/A' }}
                      </span>
                      <span
                        v-if="patient.email"
                        class="d-flex align-center gap-1"
                      >
                        <VIcon
                          icon="tabler-mail"
                          size="16"
                          color="disabled"
                        />
                        {{ patient.email || 'N/A' }}
                      </span>
                    </div>
                  </VCardText>

                  <VDivider />

                  <!-- Episodes Section -->
                  <VCardText>
                    <p class="text-subtitle-2 font-weight-semibold text-primary mb-2">
                      Episodes
                    </p>

                    <div
                      v-if="patient.episodes?.length"
                      class="episodes-list"
                    >
                      <div class="d-flex flex-column gap-2">
                        <VCard
                          v-for="episode in patient.episodes"
                          :key="episode.id"
                          class="episode-card cursor-pointer"
                          :color="selectedEpisodeId === episode.id ? 'primary' : undefined"
                          :variant="selectedEpisodeId === episode.id ? 'tonal' : 'outlined'"
                          @click="handleEpisodeSelect(patient, episode)"
                        >
                          <VCardText class="pa-3">
                            <div class="d-flex justify-space-between align-start">
                              <div>
                                <span class=" d-block">
                                  {{ episode.code }}
                                </span>
                                <span class="text-body-2">
                                  <strong>Location - </strong>
                                  {{ episode.location?.name || 'N/A' }}
                                </span>
                                <br>
                                <span class="text-body-2">
                                  <strong>Visiting Time - </strong>
                                  {{ formatDate(episode.visit_date_time) || 'N/A' }}
                                </span>
                              </div>
                              <div class="d-flex flex-column align-end justify-space-between">
                                <div>
                                  <VChip
                                    size="small"
                                    color="success"
                                    :variant="selectedEpisodeId === episode.id ? 'outlined' : 'tonal'"
                                  >
                                    {{ episode.form_type?.name || 'N/A' }}
                                  </VChip>
                                </div>
                                <AppAvatarGroup
                                  v-if="episode?.feedbacks?.length > 0"
                                  :items="episode.feedbacks"
                                  :max="2"
                                  :size="36"
                                  name-key="feedback_source.name"
                                  color-key="feedback_source.color"
                                  icon-key="feedback_source.icon"
                                  tooltip-key="feedback_source.name"
                                  tooltip-location="end"
                                  class="mt-2"
                                />
                              </div>
                            </div>
                          </VCardText>
                        </VCard>
                      </div>
                    </div>
                    <p
                      v-else
                      class="text-disabled text-body-2 mb-0"
                    >
                      No episodes available
                    </p>
                  </VCardText>
                </VCard>

                <!-- Loading More Skeleton -->
                <template v-if="isLoadingMore">
                  <VSkeletonLoader
                    v-for="i in 2"
                    :key="`skeleton-${i}`"
                    type="card"
                    height="200"
                  />
                </template>

                <!-- Load More Button -->
                <div
                  v-if="hasMore && !isFetching && !isLoadingMore"
                  class="text-center mt-4"
                >
                  <VBtn
                    variant="outlined"
                    color="primary"
                    @click="loadMore"
                  >
                    <VIcon
                      icon="tabler-reload"
                      class="me-2"
                    />
                    Load More
                  </VBtn>
                </div>
              </div>

              <!-- Empty State -->
              <div v-else>
                <div
                  class="empty-state text-center py-16 border-dashed rounded border-info"
                  style="border-color: #c4c4c4"
                >
                  <VIcon
                    icon="tabler-search"
                    size="64"
                    color="disabled"
                    class="mb-4"
                  />
                  <h3 class="text-h6 text-disabled mb-2">
                    No patients found
                  </h3>
                  <p class="text-body-2 text-disabled">
                    Search by UHID or mobile number to find patients
                  </p>
                </div>

                <VAlert
                  variant="tonal"
                  color="primary"
                  icon="tabler-info-circle"
                  class="mt-4"
                >
                  Use Advance Search to get more specific results.
                </VAlert>
              </div>
            </div>
          </div>
        </div>

        <!-- Sticky Footer Button (Popup when episode selected) -->
        <VSlideYReverseTransition>
          <div
            v-if="selectedEpisodeId"
            class="sticky-footer pa-4"
          >
            <div class="sticky-footer-content">
              <VBtn
                color="primary"
                size="large"
                block
                append-icon="tabler-arrow-right"
                @click="selectPatientEpisode"
              >
                Continue
              </VBtn>
            </div>
          </div>
        </VSlideYReverseTransition>
      </div>

      <!-- STEP 2: Feedback Form -->
      <div
        v-else-if="currentStep === 2"
        class="feedback-form-step"
      >
        <div class="feedback-form-content pa-4">
          <!-- Patient Info Header -->
          <VCard
            variant="flat"
            class="border"
          >
            <VCardText>
              <div class="d-flex flex-wrap align-center gap-4">
                <VAvatar
                  color="primary"
                  variant="tonal"
                  size="48"
                >
                  <span>{{ avatarText(selectedCustomer?.name) }}</span>
                </VAvatar>
                <div class="flex-grow-1">
                  <h6 class="text-h6 mb-1">
                    {{ selectedCustomer?.name }} ({{ selectedCustomer?.uhid }})
                  </h6>
                  <div class="text-body-2">
                    <!-- <strong>UHID:</strong> {{ selectedCustomer?.uhid }} -->
                    <!-- <span class="mx-2">|</span> -->
                    <strong>Episode:</strong> {{ selectedEpisode?.code }}
                    <span class="mx-2">|</span>
                    <strong>Location:</strong> {{ selectedEpisode?.location?.name || 'N/A' }}
                  </div>
                </div>
                <VBtn
                  variant="outlined"
                  color="secondary"
                  prepend-icon="tabler-arrow-left"
                  @click="changePatient"
                >
                  Change Patient
                </VBtn>
              </div>
            </VCardText>
          </VCard>

          <!-- Feedback Questions -->
          <div class="mt-6">
            <AssortedFeedbackQuestion
              v-if="useFeedbackEntry().currentFeedback?.form_type"
              @update:feedback-type-form-data="feedbackFormData = $event"
            />
          </div>

          <!-- Submit Button -->
          <VBtn
            v-if="useFeedbackEntry().currentFeedback?.form_type"
            class="mt-6"
            color="success"
            size="large"
            block
            :loading="loading"
            @click="onSubmit"
          >
            Submit Feedback
          </VBtn>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.ward-round-page {
  min-block-size: 100vh;
  background-color: rgb(var(--v-theme-background));
}

.ward-round-container {
  display: flex;
  justify-content: center;
}

// Patient Selection Step
.patient-selection-step {
  inline-size: 100%;
}

.patient-selection-content {
  inline-size: 100%;
  max-inline-size: 900px;
  margin-inline: auto;
}

.search-header-sticky {
  position: sticky;
  z-index: 10;
  background-color: rgb(var(--v-theme-background));
  inset-block-start: 0;
}

.sticky-footer {
  position: fixed;
  z-index: 10;
  background-color: rgb(var(--v-theme-background));
  border-block-start: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  inset-block-end: 0;
  inset-inline: 0;
}

.sticky-footer-content {
  inline-size: 100%;
  max-inline-size: 900px;
  margin-inline: auto;
}

.patient-card {
  transition: all 0.2s ease;

  &:hover {
    border-color: rgb(var(--v-theme-primary));
  }
}

.episode-card {
  transition: all 0.15s ease;

  &:hover {
    border-color: rgb(var(--v-theme-primary));
  }
}

.episodes-list {
  max-block-size: 200px;
  padding-inline-end: 4px;
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: transparent transparent;

  &:hover {
    scrollbar-color: rgba(var(--v-theme-secondary), 0.5) transparent;
  }

  &::-webkit-scrollbar {
    inline-size: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: transparent;
    border-radius: 3px;
    transition: background 0.2s ease;
  }

  &:hover::-webkit-scrollbar-thumb {
    background: rgba(var(--v-theme-secondary), 0.5);
  }

  &::-webkit-scrollbar-thumb:hover {
    background: rgba(var(--v-theme-secondary), 0.7);
  }
}

.empty-state {
  min-block-size: 300px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.feedback-status-toggle {
  block-size: 40px;
}

// Feedback Form Step
.feedback-form-step {
  inline-size: 100%;
}

.feedback-form-content {
  inline-size: 100%;
  max-inline-size: 900px;
  margin-inline: auto;
}
</style>
